// src/context/ThemeContext.tsx

import React, { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentTenant, TenantConfig } from '../config/tenants';

interface ThemeContextType {
  tenant: TenantConfig;
  refreshTenant: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantConfig>(getCurrentTenant());

  useEffect(() => {
    // Inject CSS variables for Tailwind
    const root = document.documentElement;
    root.style.setProperty('--color-primary', tenant.colors.primary);
    root.style.setProperty('--color-primary-hover', tenant.colors.primaryHover);
    root.style.setProperty('--color-accent', tenant.colors.accent);
    root.style.setProperty('--color-accent-hover', tenant.colors.accentHover);
    
    // Update meta tags
    document.title = tenant.meta.title;
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', tenant.meta.description);
    
    // Update or create og:title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', tenant.meta.title);
    
    // Update or create og:description
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', tenant.meta.description);
    
    console.log('Theme loaded:', tenant.name);
  }, [tenant]);

  const refreshTenant = () => {
    setTenant(getCurrentTenant());
  };

  return (
    <ThemeContext.Provider value={{ tenant, refreshTenant }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTenant = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTenant must be used within ThemeProvider');
  }
  return context;
};